import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Select, MenuItem, FormControl, InputLabel, Pagination, Checkbox, FormControlLabel, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

interface TableInfo {
  id: number;
  cName: string;
  cType: string;
  displayName: string;
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

interface NewRow {
  cName: string;
  cType: string;
  displayName: string;
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const groupOptions = ['Касса', 'Банк', 'Статистика'];
const dataTypes = ['INT', 'VARCHAR', 'DATE', 'FLOAT'];
const ROWS_PER_PAGE = 15;

const Actions: React.FC = () => {
  const [tableInfo, setTableInfo] = useState<TableInfo[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [newRow, setNewRow] = useState<NewRow>({ cName: '', cType: '', displayName: '', InputBlock: 0, formula: '', sortByGroup: '', important: 0, statisticsHide: 0, hideInStatTable: 0 });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<NewRow>>({});
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormulaChecked, setIsFormulaChecked] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<TableInfo | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    document.title = 'Пользовательская таблица | Zerde';
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const logAction = async (action: string) => {
    const currentRole = localStorage.getItem('role') || 'unknown';
    const currentUsername = localStorage.getItem('username') || 'unknown';
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/log', { role: currentRole, username: currentUsername, action });
    } catch (error) {
      console.error('Ошибка при логировании действия:', error);
    }
  };


  useEffect(() => {
    fetchTableInfo();
    // eslint-disable-next-line
  }, []);

  const fetchTableInfo = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/table-info');
      setTableInfo(response.data);
    } catch (error) {
      console.error('Ошибка при получении информации о таблице:', error);
      showNotification('Не удалось загрузить информацию о таблице', 'error');
    }
  };

  // const handleDelete = async (id: number) => {
  //   if (window.confirm('Вы уверены, что хотите удалить эту строку?')) {
  //     try {
  //       await axios.delete(`https://tbl.zerdegroup.kz/api/table-info/${id}`);
  //       logAction(`удалил колонку с ID ${id} | Таблицы`);
  //       fetchTableInfo();
  //     } catch (error) {
  //       console.error('Ошибка при удалении строки:', error);
  //       alert('Не удалось удалить строку');
  //     }
  //   }
  // };

  const handleOpenDeleteDialog = (row: TableInfo) => {
    setRowToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRowToDelete(null);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      try {
        await axios.delete(`https://tbl.zerdegroup.kz/api/table-info/${rowToDelete.id}`);
        showNotification(`Колонка "${rowToDelete.displayName}" успешно удалена`, 'success');
        await logAction(`удалил колонку "${rowToDelete.displayName}" | Таблицы`);
        fetchTableInfo();
      } catch (error) {
        console.error('Ошибка при удалении строки:', error);
        showNotification('Не удалось удалить колонку', 'error');
      }
      handleCloseDeleteDialog();
    }
  };

  const handleOpenForm = (row?: TableInfo) => {
    if (row) {
      setNewRow({ ...row });
      setEditingId(row.id);
      setIsFormulaChecked(!!row.formula);
    } else {
      setNewRow({ cName: '', cType: '', displayName: '', InputBlock: 0, formula: '', sortByGroup: '', important: 0, statisticsHide: 0, hideInStatTable: 0 });
      setEditingId(null);
      setIsFormulaChecked(false);
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setNewRow({ cName: '', cType: '', displayName: '', InputBlock: 0, formula: '', sortByGroup: '', important: 0, statisticsHide: 0, hideInStatTable: 0 });
    setErrors({});
    setEditingId(null);
    setIsFormulaChecked(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<NewRow> = {};
    if (!newRow.cName.trim()) newErrors.cName = 'Обязательное поле';
    if (!newRow.cType) newErrors.cType = 'Обязательное поле';
    if (!newRow.displayName.trim()) newErrors.displayName = 'Обязательное поле';
    if (!newRow.sortByGroup) newErrors.sortByGroup = 'Обязательное поле';
    if (isFormulaChecked && !newRow.formula.trim()) newErrors.formula = 'Формула обязательна, если выбрана';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitForm = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`https://tbl.zerdegroup.kz/api/table-info/${editingId}`, newRow);
        showNotification(`Колонка "${newRow.displayName}" успешно обновлена`, 'success');
        await logAction(`отредактировал колонку "${newRow.displayName}" | Таблица`);
      } else {
        await axios.post('https://tbl.zerdegroup.kz/api/table-info', newRow);
        showNotification(`Колонка "${newRow.displayName}" успешно создана`, 'success');
        await logAction(`добавил новую колонку "${newRow.displayName}" | Таблица`);
      }
      await fetchTableInfo();
      handleCloseForm();
    } catch (error) {
      console.error('Ошибка при сохранении данных:', error);
      showNotification('Не удалось сохранить данные', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = (): boolean => {
    return newRow.cName.trim() !== '' && newRow.cType !== '' && newRow.displayName.trim() !== '' && (!isFormulaChecked || newRow.formula.trim() !== '');
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const paginatedTableInfo = tableInfo.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenForm()}
          sx={{ position: 'absolute', bottom: 15, right: 15 }}
        >
          <AddIcon />
        </Button>
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Имя в БД</TableCell>
              <TableCell>Отображаемое имя</TableCell>
              <TableCell>Обязательное</TableCell>
              <TableCell>Группа</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Блок</TableCell>
              <TableCell>Формула</TableCell>
              <TableCell>Итоги</TableCell>
              <TableCell>Скрыть</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTableInfo.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ width: '50px' }}>{row.id}</TableCell>
                <TableCell sx={{ width: '200px' }}>{row.cName}</TableCell>
                <TableCell sx={{ width: '250px' }}>{row.displayName}</TableCell>
                <TableCell sx={{ width: '100px' }}>{row.important === 1 ? 'Да' : 'Нет'}</TableCell>
                <TableCell sx={{ width: '100px' }}>{row.sortByGroup}</TableCell>
                <TableCell sx={{ width: '70px' }}>{row.cType}</TableCell>
                <TableCell sx={{ width: '40px' }}>{row.InputBlock === 1 ? 'Да' : 'Нет'}</TableCell>
                <TableCell sx={{ width: '40px' }}>{row.formula ? 'Да' : 'Нет'}</TableCell>
                <TableCell sx={{ width: '40px' }}>{row.statisticsHide === 1 ? 'Да' : 'Нет'}</TableCell>
                <TableCell sx={{ width: '40px' }}>{row.hideInStatTable === 1 ? 'Да' : 'Нет'}</TableCell>
                <TableCell>
                  <Button variant="contained" color="primary" onClick={() => handleOpenForm(row)} sx={{ mr: 1 }}>
                    <EditIcon />
                  </Button>
                  <Button variant="contained" color="secondary" onClick={() => handleOpenDeleteDialog(row)}>
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          count={Math.ceil(tableInfo.length / ROWS_PER_PAGE)}
          page={page}
          onChange={handleChangePage}
          color="primary"
          sx={{pt: 2, display: 'flex' ,justifyContent: 'center'}}
        />
      </TableContainer>

      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Редактировать колонку' : 'Добавить новую колонку'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Имя для БД"
            fullWidth
            value={newRow.cName}
            onChange={(e) => setNewRow({ ...newRow, cName: e.target.value })}
            error={!!errors.cName}
            helperText={errors.cName}
          />
          <FormControl fullWidth margin="dense" error={!!errors.cType}>
            <InputLabel id="data-type-label">Тип данных</InputLabel>
            <Select
              labelId="data-type-label"
              value={newRow.cType}
              onChange={(e) => setNewRow({ ...newRow, cType: e.target.value })}
              label="Тип данных"
            >
              {dataTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            {errors.cType && <Typography color="error">{errors.cType}</Typography>}
          </FormControl>

          <FormControl fullWidth margin="dense" error={!!errors.sortByGroup}>
            <InputLabel id="group-label">Группировка</InputLabel>
            <Select
              labelId="group-label"
              value={newRow.sortByGroup}
              onChange={(e) => setNewRow({ ...newRow, sortByGroup: e.target.value })}
              label="Группировка"
            >
              {groupOptions.map((group) => (
                <MenuItem key={group} value={group}>{group}</MenuItem>
              ))}
            </Select>
            {errors.sortByGroup && <Typography color="error">{errors.sortByGroup}</Typography>}
          </FormControl>

          <TextField
            margin="dense"
            label="Отображаемое имя"
            fullWidth
            value={newRow.displayName}
            onChange={(e) => setNewRow({ ...newRow, displayName: e.target.value })}
            error={!!errors.displayName}
            helperText={errors.displayName}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRow.InputBlock === 1}
                onChange={(e) => setNewRow({ ...newRow, InputBlock: e.target.checked ? 1 : 0 })}
              />
            }
            label="Заблокировать поле"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRow.important === 1}
                onChange={(e) => setNewRow({ ...newRow, important: e.target.checked ? 1 : 0 })}
              />
            }
            label="Обязательное поле"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRow.statisticsHide === 1}
                onChange={(e) => setNewRow({ ...newRow, statisticsHide: e.target.checked ? 1 : 0 })}
              />
            }
            label="Скрыть итоги"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newRow.hideInStatTable === 1}
                onChange={(e) => setNewRow({ ...newRow, hideInStatTable: e.target.checked ? 1 : 0 })}
              />
            }
            label="Не отображать"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isFormulaChecked}
                onChange={(e) => {
                  setIsFormulaChecked(e.target.checked);
                  if (!e.target.checked) {
                    setNewRow({ ...newRow, formula: '' });
                  }
                }}
              />
            }
            label="Формула"
          />
          {isFormulaChecked && (
            <TextField
              margin="dense"
              label="Формула"
              fullWidth
              value={newRow.formula}
              onChange={(e) => setNewRow({ ...newRow, formula: e.target.value })}
              error={!!errors.formula}
              helperText={errors.formula}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 15px' }}>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            color="primary"
            disabled={!isFormValid() || loading}
          >
            {loading ? <CircularProgress size={24} /> : (editingId ? 'Обновить' : 'Создать')}
          </Button>
          <Button onClick={handleCloseForm} variant="outlined" color="primary">
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить колонку <b>{rowToDelete?.displayName}</b>?
            <br /> Действие нельзя отменить!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            <b>Отмена</b>
          </Button>
          <Button onClick={confirmDelete} color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Actions;